import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { UserStore } from "src/app/core/stores/user.store";
import { LatLon } from "src/app/shared/components/bubble-map/bubble-map.component";
import { UtilsService } from "src/app/shared/services/utils.service";
import { Place, PlaceStates } from "../models/place.model";
import { OrderByDirection, OrderByItem } from "../models/search-result.model";
import { PlaceService } from "../services/place.service";
import { AppStore, PlaceTourScenario } from "./app.store";
import { AuthStore } from "./auth.store";

enum PlaceScreenMode {
  Normal = "normal",
  Loading = "loading",
}

const LOG_TAG = "[PLACES-STORE] ";

@Injectable({
  providedIn: "root",
})
export class PlacesStore {
  private placeService: PlaceService = inject(PlaceService);
  private utilsService: UtilsService = inject(UtilsService);
  protected userStore: UserStore = inject(UserStore);
  private authStore: AuthStore = inject(AuthStore);
  private appStore: AppStore = inject(AppStore);
  private router: Router = inject(Router);

  private screenMode: PlaceScreenMode = PlaceScreenMode.Normal;

  private readonly forceRefresh = signal<boolean>(false);
  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  triggerForcedRefresh(): void {
    this.forceRefresh.set(true);
  }

  // ---------------------------
  // --- touring
  // ---------------------------

  readonly touring = {
    welcomeStep: {
      url: "places",
      title: $localize`:@@places.touring.welcomeTitle:Welcome to places section`,
      text: $localize`:@@places.touring.welcomeText:You will learn how to track the water consumption by places, as well as manage your places and sub-places. Let's start with consumption tracking.`,
    },
    periodFilterStep: {
      url: "places",
      title: $localize`:@@places.touring.periodSelectionTitle:Choice of the period`,
      text: $localize`:@@places.touring.periodSelectionText:Whether for the entire year or the details of a single day, feel free to explore the possibilities.`,
    },
    placesStep: {
      url: "places",
      title: $localize`:@@places.touring.yourPlacesTitle:Your places`,
      text: $localize`:@@places.touring.yourPlacesText:Select the place(s) whose consumption & location you want to display. Some more detailed information are only visible if a single place is selected.`,
    },
    globalViewStep: {
      url: "places",
      title: $localize`:@@places.touring.yourConsumptionTitle:Your consumption`,
      text: $localize`:@@places.touring.yourConsumptionText:If many places are selected, you can see them on the map and their global consumption based on the filters seen previously.`,
    },
    placeStep: {
      url: "places/100",
      title: $localize`:@@places.touring.placeManagementTitle:Management of a place`,
      text: $localize`:@@places.touring.placeManagementText:After selected one place, you can see the list of this place's devices, view their information, update place details and add new sub-place.`,
    },
  };

  readonly tourSteps$ = computed(() => {
    const tourSteps = [];

    Object.entries(this.touring).forEach(([step, data]) => {
      tourSteps.push(step + "@" + data.url);
    });

    return tourSteps;
  });

  getTourTitle(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.title : "";
  }

  getTourText(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.text : "";
  }

  // ---------------------------
  // --- page header
  // ---------------------------

  readonly headers$ = computed(() => {
    const headers = [];

    headers.push({
      id: "dashboard",
      label: $localize`Places`,
      url: "/places",
    });

    return headers;
  });

  // --------------------
  // ---- places
  // --------------------

  private tourAddId: number = 90;
  private placesLastFetch = signal<Date>(null);
  private placesRefreshDelay: number = 3600;

  private readonly places = signal<Place[]>([]);
  private readonly placesById = signal<Record<number, Place>>({});

  private tourPlaces: Place[] = [];
  private tourPlacesById: Record<number, Place> = {};

  private readonly placesLoading = signal(false);
  readonly placesLoading$ = computed(() => {
    return this.placesLastFetch() === null || this.placesLoading() === true;
  });

  // Getter signals for external access
  readonly places$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourPlaces : this.places();
  });

  readonly placeIds$ = computed(() => {
    const placesLoading = this.placesLoading$();
    const placesById = this.placesById$();

    if (placesLoading) return [];

    return Object.keys(placesById).map((placeId) => parseInt(placeId));
  });
  readonly placesCount$ = computed(() => {
    const placeIds = this.placeIds$();

    return placeIds.length;
  });
  readonly noPlaces$ = computed(() => {
    const placesCount = this.placesCount$();
    const placesLoading = this.placesLoading$();

    if (placesLoading) return false;

    return placesCount === 0;
  });
  readonly hasPlaces$ = computed(() => {
    const places = this.places$();

    return Array.isArray(places) && places.length > 0;
  });
  readonly placesById$ = computed(() => {
    const placesById = this.placesById();
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourPlacesById : placesById;
  });
  readonly rootPlaceIds$ = computed(() => {
    const placesLoading = this.placesLoading$();
    const places = this.places$();

    const ids = [];
    places.map((place) => {
      ids.push(place.id);
    });

    return ids;
  });
  readonly placesWithAllByIds$ = computed(() => {
    const placesById = this.placesById$();

    const result = {};
    Object.entries(placesById).forEach(([placeId, place]) => {
      result[placeId] = place.clone();
    });

    result[-1] = new Place(
      -1,
      $localize`without place`,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      0
    );

    result["all"] = new Place(
      0,
      $localize`all places`,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      0
    );

    return result;
  });
  readonly placesWithAll$ = computed(() => {
    const places = this.places$();
    const featWithoutPlace = this.appStore.enableWithoutPlace();

    if (places.length === 0) return places;

    const placesWithAll = [...places];

    if (featWithoutPlace === true) {
      // ads no place "Sans emplacement"
      placesWithAll.unshift(
        new Place(
          -1,
          $localize`without place`,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          0
        )
      );
    }

    // add first place = "Tous les emplacements"
    let all_anomalies_count = 0; //TODO
    placesWithAll.unshift(
      new Place(
        0,
        $localize`all places`,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        all_anomalies_count
      )
    );

    return placesWithAll;
  });

  // Fetch items from an API
  fetchPlaces(forceRefresh: boolean = false): void {
    if (this.placesLoading()) return;

    if (
      forceRefresh ||
      !this.placesLastFetch() ||
      this.utilsService.calculateElapsedSeconds(this.placesLastFetch()) >
        this.placesRefreshDelay
    ) {
      this.placesLoading.set(true);
      this.error.set(null);

      this.placeService.getPlacesForStore().subscribe({
        next: (places: Place[]) => {
          this.places.set(places);
          this.placesById.set(this._computeIndexedPlaces(places));
          if (this.screenMode !== PlaceScreenMode.Loading) {
            this.placesLoading.set(false);
          }
          this.placesLastFetch.set(new Date());
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to fetch places : ", err);
          this.error.set(err.message || "An error occurred.");
          if (this.screenMode !== PlaceScreenMode.Loading) {
            this.placesLoading.set(false);
          }
        },
      });
    }
  }

  getPlaceDescendantIds(placeId: number): number[] {
    if (placeId === -1) return [];

    let placesById = this.placesById$();

    return placesById[placeId] ? placesById[placeId].deepChildrenIds : [];
  }

  getPlaceAncestorIds(placeId: number): number[] {
    if (placeId === -1) return [];

    let placesById = this.placesById$();

    return placesById[placeId] ? placesById[placeId].ancestorIds : [];
  }

  getFirstLevelPlaces(placeId: number): Place[] {
    if (placeId === -1) return [];

    if (placeId === null) {
      return this.places$();
    } else {
      const tourMode = this.appStore.tourMode$();
      let placesById;
      if (tourMode) {
        placesById = this.tourPlacesById;
      } else {
        placesById = this.placesById();
      }
      return placesById[placeId] ? placesById[placeId].children : [];
    }
  }

  getFirstLevelPlacesFromList(placeIds: number[]): Place[] {
    if (!Array.isArray(placeIds) || placeIds.length === 0) return [];

    const places: Place[] = [];
    const placesById = this.placesById$();

    placeIds.map((placeId) => {
      const place = placesById[placeId];
      if (!place) return;

      if (place.ancestorIds.length === 0) {
        places.push(place);
      }
    });

    return places;
  }

  getLabel(placeId): string {
    const noLabel = $localize`no place`;

    if (!placeId) return noLabel;

    let placesById = this.placesById$();

    const place = placesById[placeId] ? placesById[placeId] : null;
    if (!place) {
      console.log(LOG_TAG + "getLabel => can't find place !!!", placeId);
      return noLabel;
    }

    const labelParts = [];
    place.ancestorIds.map((ancestorId) => {
      const ancestor = placesById[ancestorId];
      if (!ancestor) return;

      labelParts.push(ancestor.label);
    });
    labelParts.push(place.label);

    return labelParts.join(" / ");
  }

  getLocation(placeId): LatLon {
    if (!placeId) return null;

    const placesById = this.placesById$();
    let place = placesById[placeId];

    if (!place) return null;

    if (place.parent_id !== null) {
      const rootPlaceId = place.ancestorIds[0];
      if (rootPlaceId) place = placesById[rootPlaceId];
      if (!place) return null;
    }

    return place.coordinates;
  }

  getRootPlace(placeId): Place {
    if (!placeId) return null;

    const placesById = this.placesById$();
    let place = placesById[placeId];

    if (!place) return null;

    if (place.parent_id !== null) {
      const rootPlaceId = place.ancestorIds[0];
      if (rootPlaceId) place = placesById[rootPlaceId];
      if (!place) return null;
    }
    return place;
  }

  // ---------------------------
  // --- sorting
  // ---------------------------

  private placesOrderByTimer: any;

  private readonly placesOrderBy = signal<string>("label-asc");
  readonly placesOrderBy$ = this.placesOrderBy.asReadonly();

  readonly sortsById$ = computed(() => {
    const sorts: OrderByItem[] = [
      {
        id: "label-asc",
        field: "label",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.aToZ:(A > Z)`,
      },
      {
        id: "label-desc",
        field: "label",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.zToA:(Z > A)`,
      },
    ];

    const sortItems = {};
    sorts.map((sort) => {
      sortItems[sort.id] = sort;
    });

    return sortItems;
  });

  readonly sorts$ = computed(() => {
    const sortsById = this.sortsById$();

    return Object.values(sortsById);
  });

  readonly currentSort$ = computed(() => {
    const sortsById = this.sortsById$();
    const orderBy = this.placesOrderBy$();

    if (!orderBy) return null;

    return sortsById[orderBy];
  });

  updatePlacesOrderBy(newOrderBy: string): void {
    if (newOrderBy !== this.placesOrderBy()) {
      if (this.placesOrderByTimer) {
        clearTimeout(this.placesOrderByTimer);
      }

      this.placesOrderByTimer = setTimeout(() => {
        this.placesOrderBy.set(newOrderBy);
      }, 100);
    }
  }

  // --------------------
  // ---- selection
  // --------------------

  private placesSelectedTimer: any;

  private readonly placesSelected = signal<number[]>([]);
  readonly placesSelected$ = this.placesSelected.asReadonly();

  private readonly unselectRecently = signal<boolean>(false);
  readonly unselectRecently$ = this.unselectRecently.asReadonly();

  readonly hasIncoherentSelection$ = computed(() => {
    const rootPlaceIds = this.rootPlaceIds$();
    const selectedIds = this.placesSelected$();
    const placesById = this.placesById$();

    if (rootPlaceIds.length === 0 || selectedIds.length === 0) return false;

    // prepare "wrong id list" (descendants of root places)
    const placeDescendantIds = [];
    rootPlaceIds.map((id) => {
      const rootPlace = placesById[id];
      if (!rootPlace) return;

      if (
        Array.isArray(rootPlace.deepChildrenIds) &&
        rootPlace.deepChildrenIds.length > 0
      ) {
        rootPlace.deepChildrenIds.map((childrenId) => {
          if (placeDescendantIds.indexOf(childrenId) < 0) {
            placeDescendantIds.push(childrenId);
          }
        });
      }
    });

    // check selected
    const wrongSelectedIds = selectedIds.filter((selectedId) => {
      const selectedPlace = placesById[selectedId];

      if (!selectedPlace) return false;
      return (
        selectedPlace.parent_id !== null &&
        selectedIds.indexOf(selectedPlace.parent_id) >= 0 &&
        placeDescendantIds.indexOf(selectedId) >= 0
      );
    });

    return wrongSelectedIds.length > 0;
  });

  readonly place$ = computed(() => {
    const placesById = this.placesById$();
    const placeIds = this.placeIds$();
    const placesSelected = this.placesSelected();
    if (
      placesSelected.length !== 1 ||
      Object.keys(placesById).length === 0 ||
      placeIds.indexOf(placesSelected[0]) < 0
    )
      return null;

    return placesById[placesSelected[0]];
  });

  updatePlacesSelected(placeIds: number[]) {
    //TODO vérifier si placeIds a changé

    if (this.placesSelectedTimer) {
      clearTimeout(this.placesSelectedTimer);
    }

    this.placesSelectedTimer = setTimeout(() => {
      this.placesSelected.set(placeIds);
    }, 100);
  }

  selectPlace(placeId: number) {
    if (this.placesSelected().indexOf(placeId) < 0) {
      const selected = [...this.placesSelected()];
      selected.push(placeId);
      this.placesSelected.set(selected);
    }
  }

  selectNone() {
    if (this.placesSelected().length > 0) {
      this.placesSelected.set([]);
      this.unselectRecently.set(true);
    }
  }

  unselectPlace(placeId: number) {
    if (this.placesSelected().indexOf(placeId) >= 0) {
      const selected = this.placesSelected().filter(
        (selectedId) => selectedId !== placeId
      );
      this.unselectRecently.set(true);
      this.placesSelected.set(selected);
    }
  }

  resetUnselectRecently() {
    if (this.unselectRecently() === true) {
      this.unselectRecently.set(false);
    }
  }

  async updatePlaceToShowInUrl(placeId: number): Promise<void> {
    await this.router.navigate([placeId ? "/places/" + placeId : "/places"], {
      replaceUrl: true,
      // queryParams: {
      //   selected: placeId,
      // },
      queryParamsHandling: "merge",
    });
  }

  // --------------------
  // ---- filtering
  // --------------------

  private placesSearchTimer: any;

  private readonly placesSearch = signal<string>(null);
  readonly placeSearch$ = this.placesSearch.asReadonly();

  updatePlacesSearch(newSearch: string): void {
    if (newSearch !== this.placesSearch()) {
      if (this.placesSearchTimer) {
        clearTimeout(this.placesSearchTimer);
      }

      this.placesSearchTimer = setTimeout(() => {
        this.placesSearch.set(newSearch);
      }, 100);
    }
  }

  readonly filteredPlaces$ = computed(() => {
    const placesById = this.placesById$();
    let filteredPlaces = this.places$();
    const search = this.placesSearch();
    // const stats = this.placesStats$();
    const currentSort = this.currentSort$();
    const user = this.userStore.user$();

    if (filteredPlaces.length === 0) return filteredPlaces;

    filteredPlaces = filteredPlaces.map((place) => place.clone());

    // filter on search
    const fixedSearch =
      typeof search === "string" && search.trim().length > 0
        ? search.trim().toLowerCase()
        : null;
    if (fixedSearch) {
      filteredPlaces = filteredPlaces.filter(
        (place) => place.label.trim().toLowerCase().indexOf(fixedSearch) >= 0
      );
      if (filteredPlaces.length === 0) return filteredPlaces;
    }

    // apply order by
    if (currentSort) {
      if (currentSort.field === "label") {
        if (currentSort.direction === OrderByDirection.asc) {
          filteredPlaces = filteredPlaces.sort((placeA, placeB) =>
            placeA.label.localeCompare(placeB.label)
          );
        } else {
          filteredPlaces = filteredPlaces.sort((placeA, placeB) =>
            placeB.label.localeCompare(placeA.label)
          );
        }
      }
    }

    // console.log(LOG_TAG + "filtered places", filteredPlaces);

    return filteredPlaces;
  });

  // ---------------------------
  // --- actions
  // ---------------------------

  private readonly adding = signal<boolean>(false);
  readonly adding$ = this.adding.asReadonly();
  private readonly addResult = signal<Place | string | null>(null);
  readonly addResult$ = this.addResult.asReadonly();

  addPlace(
    label: string,
    description?: string,
    location?: LatLon,
    parentId?: number
  ) {
    if (this.adding()) return;

    this.addResult.set(null);
    this.adding.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // prepare new place
      const addedPlace = this._createPlace(
        this.tourAddId,
        label,
        0,
        location.lat,
        location.lon,
        description
      );

      // add place to parent
      if (parentId) {
        const parentPlace = this.tourPlacesById[parentId];
        if (!parentPlace) {
          this.addResult.set("parent_not_found");
        }
        this._addChildPlace(parentPlace, addedPlace);
      }

      // update stores
      this.tourPlacesById[addedPlace.id] = addedPlace;
      this.tourAddId++;

      this.adding.set(false);
      this.addResult.set(addedPlace);
    } else {
      this.placeService
        .addPlaceForStore(label, description, location, parentId)
        .subscribe({
          next: (place: Place) => {
            this.adding.set(false);
            this.addResult.set(place);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to add place : ", err);
            this.addResult.set(err.error_code || "unknown");
            this.adding.set(false);
          },
        });
    }
  }

  declareAddPlaceSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh places
      this.fetchPlaces(true);
    }
    this.addResult.set(null);
  }

  resetAddResult() {
    this.addResult.set(null);
  }

  private readonly updating = signal<boolean>(false);
  readonly updating$ = this.updating.asReadonly();
  private readonly updateResult = signal<Place | string | null>(null);
  readonly updateResult$ = this.updateResult.asReadonly();

  updatePlace(
    placeId: number,
    label: string,
    description?: string,
    location?: LatLon,
    parentId?: number
  ) {
    if (this.updating()) return;

    this.updateResult.set(null);
    this.updating.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // get place
      const updatedItem = this.placesById[placeId];

      // update place props
      updatedItem.label = label;
      updatedItem.description = description;
      updatedItem.latitude = location ? location.lat : null;
      updatedItem.longitude = location ? location.lon : null;

      const oldParentId = updatedItem.parent_id;
      updatedItem.parent_id = parentId;
      //TODO adapt parent & children
      // if (parentId) {
      //   const parentPlace = this.tourPlacesById[parentId];
      //   if (!parentPlace) {
      //     this.placeUpdateResult.set("parent_not_found");
      //   }
      //   this._addChildPlace(parentPlace, addedPlace);
      // }

      // update stores

      this.updating.set(false);
      this.updateResult.set(updatedItem);
    } else {
      this.placeService
        .updatePlaceForStore(placeId, label, description, location, parentId)
        .subscribe({
          next: (place: Place) => {
            this.updating.set(false);
            this.updateResult.set(place);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to update place : ", err);
            this.updateResult.set(err.error_code || "unknown");
            this.updating.set(false);
          },
        });
    }
  }

  declareUpdateSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh items
      this.fetchPlaces(true);
    }
    this.updateResult.set(null);
  }

  resetUpdateResult() {
    this.updateResult.set(null);
  }

  private readonly removing = signal<boolean>(false);
  readonly removing$ = this.removing.asReadonly();
  private readonly removeResult = signal<boolean | string | null>(null);
  readonly removeResult$ = this.removeResult.asReadonly();

  removePlace(placeId: number) {
    if (this.removing()) return;

    this.removeResult.set(null);
    this.removing.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // check placeId in store
      let exists = Object.keys(this.tourPlacesById).filter(
        (id) => parseInt(id) === placeId
      );
      if (!exists || exists.length === 0) {
        this.removeResult.set("unauthorized");
      }

      // remove place
      const place = this.tourPlacesById[placeId];
      const parentPlaceId = place.parent_id;
      if (parentPlaceId) {
        const parentPlace = this.tourPlacesById[parentPlaceId];
        this._removeChildPlace(parentPlace, place);
        delete this.tourPlacesById[place.id];
      } else if (Array.isArray(place.children) && place.children.length > 0) {
        place.children.map((child) => {
          child.parent_id = null;
          this.tourPlaces.push(child);
        });
        this.tourPlacesById = this._computeIndexedPlaces(this.tourPlaces);
      }

      // update stores

      this.removing.set(false);
      this.removeResult.set(true);
    } else {
      this.placeService.removePlaceForStore(placeId).subscribe({
        next: (place: Place) => {
          this.removing.set(false);
          this.removeResult.set(true);
        },
        error: (err) => {
          console.error(LOG_TAG + "failed to remove place : ", err);
          this.removeResult.set(err.error_code || "unknown");
          this.removing.set(false);
        },
      });
    }
  }

  declareRemoveSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh places
      this.fetchPlaces(true);
    }
    this.removeResult.set(null);
  }

  resetRemoveResult() {
    this.removeResult.set(null);
  }

  // --------------------
  // ---- navigation
  // --------------------

  private readonly placesState = signal<PlaceStates>({});
  readonly placeStates$ = this.placesState.asReadonly();

  togglePlaceState(placeId: number): void {
    const states = this.placeStates$();

    states[placeId] = !states[placeId];
    this.placesState.set(states);
  }

  // --------------------
  // ---- LIFE CYCLE METHODS
  // --------------------

  public constructor() {
    this._prepareTourData();

    effect(
      () => {
        const forcedRefresh = this.forceRefresh();
        if (forcedRefresh === true) {
          this.fetchPlaces(true);
          this.forceRefresh.set(false);
        }
      },
      { allowSignalWrites: true }
    );
  }

  public clear() {
    this.forceRefresh.set(false);
    this.error.set(null);

    this.placesLastFetch.set(null);
    this.places.set([]);
    this.placesById.set({});
    this.placesLoading.set(false);

    if (this.placesOrderByTimer) clearTimeout(this.placesOrderByTimer);
    this.placesOrderBy.set("label-asc");

    if (this.placesSelectedTimer) clearTimeout(this.placesSelectedTimer);
    this.placesSelected.set([]);
    this.unselectRecently.set(false);

    if (this.placesSearchTimer) clearTimeout(this.placesSearchTimer);
    this.placesSearch.set(null);

    this.adding.set(false);
    this.addResult.set(null);

    this.updating.set(false);
    this.updateResult.set(null);

    this.removing.set(false);
    this.removeResult.set(null);

    this.placesState.set({});
  }

  // --------------------
  // ---- PRIVATE METHODS
  // --------------------

  private _computeIndexedPlaces(places: Place[]): Record<number, Place> {
    const index: Record<number, Place> = {};

    function registerPlace(
      place: Place,
      parentAncestors: number[] = [],
      deepLevel: number = 0
    ) {
      if (!index[place.id]) {
        place.ancestorIds = [...parentAncestors];
        place.deepLevel = deepLevel;
        index[place.id] = place;
      }

      // update childrenIds of parent
      if (place.parent_id && index[place.parent_id]) {
        index[place.parent_id].childrenIds.push(place.id);
      }

      // compute recursively chidlren
      if (place.children) {
        for (const child of place.children) {
          registerPlace(
            child,
            [...index[place.id].ancestorIds, place.id],
            deepLevel + 1
          );
          index[place.id].deepChildrenIds.push(child.id);
          index[place.id].deepChildrenIds.push(
            ...index[child.id].deepChildrenIds
          );
        }
      }
    }

    // recursive analyze of places
    places.forEach((place) => registerPlace(place));

    return index;
  }

  private _prepareParisPlaces(): void {
    const paris = this._createPlace(100, "Paris", 2, 48.86690178, 2.31496081);

    const zone1 = this._createPlace(101, "Zone 1");
    const zone2 = this._createPlace(102, "Zone 2");
    const building = this._createPlace(103, "Building");
    const floorP2 = this._createPlace(104, "FloorP2");

    this._addChildPlace(building, floorP2);

    this._addChildPlace(zone2, building);

    this._addChildPlace(paris, zone1);
    this._addChildPlace(paris, zone2);
    this.tourPlaces.push(paris);
  }

  private _prepareGrenoblePlaces(): void {
    const grenoble = this._createPlace(
      1,
      "Grenoble",
      0,
      45.18616545,
      5.7336987
    );

    const camping = this._createPlace(2, "Camping");
    const swimmingPool = this._createPlace(3, "Swimming Pool");
    const premium = this._createPlace(4, "Premium");

    this._addChildPlace(grenoble, camping);

    this._addChildPlace(camping, swimmingPool);
    this._addChildPlace(camping, premium);

    const home = this._createPlace(5, "Home");
    const floor0 = this._createPlace(6, "Floor 0");
    const floor1 = this._createPlace(7, "Floor 1");

    this._addChildPlace(grenoble, home);

    this._addChildPlace(home, floor0);
    this._addChildPlace(home, floor1);

    this.tourPlaces.push(grenoble);
  }

  private _prepareOnePlace() {
    const grenoble = this._createPlace(
      1,
      "Grenoble",
      0,
      45.18616545,
      5.7336987
    );
    this.tourPlaces.push(grenoble);
  }

  private _prepareTestPlaces(): void {
    const grenoble = this._createPlace(
      1,
      "Grenoble",
      0,
      45.18616545,
      5.7336987
    );
    const paris = this._createPlace(
      2,
      "Hotel Paris",
      0,
      48.86690178,
      2.31496081
    );
    const floor1 = this._createPlace(3, "First floor");
    const floor2 = this._createPlace(4, "Second floor");
    const newyork = this._createPlace(5, "New York", 0, 40.7141667, -74.00583);

    this._addChildPlace(paris, floor1);
    this._addChildPlace(paris, floor2);

    this.tourPlaces.push(grenoble);
    this.tourPlaces.push(paris);
    this.tourPlaces.push(newyork);
  }

  private _prepareTourData(): void {
    switch (this.appStore.placesScenario) {
      case PlaceTourScenario.None:
        // nothing to do
        break;
      case PlaceTourScenario.One:
        this._prepareOnePlace();
        break;
      case PlaceTourScenario.Full:
        this._prepareGrenoblePlaces();
        this._prepareParisPlaces();
        //TODO Lyon
        break;
      case PlaceTourScenario.Test:
        this._prepareTestPlaces();
        break;
    }
    this.tourPlacesById = this._computeIndexedPlaces(this.tourPlaces);
  }

  private _createPlace(
    id: number,
    label: string,
    anomalies: number = 0,
    latitude: number = null,
    longitude: number = null,
    desc: string = null
  ): Place {
    return new Place(
      id,
      label,
      label,
      desc,
      latitude,
      longitude,
      1,
      null,
      null,
      anomalies
    );
  }

  private _addChildPlace(parent: Place, child: Place) {
    child.full_path = parent.full_path + "/" + child.label;
    child.hierarchy_level = parent.hierarchy_level + 1;
    child.parent_id = parent.id;

    if (!parent.children) {
      parent.children = [];
    }
    parent.children.push(child);
  }

  private _removeChildPlace(parent: Place, child: Place) {
    parent.children = parent.children.filter(
      (parentChild) => parentChild.id !== child.id
    );
    if (Array.isArray(child.children) && child.children.length > 0) {
      child.children.map((childChild) => {
        this._addChildPlace(parent, childChild);
      });
    }
    child.parent_id = null;
    child.children = [];
  }
}
